/**
 * Fetches this account's own preferred capture/enrichment tier once, on mount, and keeps every
 * mounted `CaptureTierPicker` in sync with it live — no existing frontend mechanism keeps
 * multiple instances of *server-backed* state in sync (`settings.ts`'s `useSyncExternalStore`
 * is client-only localStorage; `useAiTierAvailability` fetches independently per mount), so
 * this is new. `setTier` updates optimistically and rolls back on failure, re-throwing so the
 * picker that called it can show its own error the same way every other card does.
 */

import { useCallback, useEffect, useState } from "react";
import { api, type CaptureTier } from "./api";
import { CaptureTierContext } from "./captureTierContext";

export function CaptureTierProvider({ children }: { children: React.ReactNode }) {
  const [tier, setTierState] = useState<CaptureTier | null>(null);

  useEffect(() => {
    void api.getCaptureTier().then((r) => setTierState(r.tier));
  }, []);

  const setTier = useCallback(
    async (next: CaptureTier) => {
      const previous = tier;
      setTierState(next);
      try {
        await api.setCaptureTier(next);
      } catch (err) {
        setTierState(previous);
        throw err;
      }
    },
    [tier],
  );

  return (
    <CaptureTierContext.Provider value={{ tier, setTier }}>{children}</CaptureTierContext.Provider>
  );
}
